import { conversationApi } from "api/conversation.api";
import { action, computed, makeAutoObservable, runInAction } from "mobx";
import type { Conversation } from "types/conversation";
import type { ChatFile } from "types/chat.type";
import type { Message } from "types/message";
import { MessageRole } from "types/message";
import type { Project } from "types/project";
import type { Staff } from "types/staff";
import { normalizeMessages } from "utils/messageAttachments";

const nowUnix = () => Math.floor(Date.now() / 1000);

/** Negative temp ids — must be unique so stream tokens never hit the user bubble. */
let tempMessageIdSeq = 0;
const nextTempMessageId = () => {
  tempMessageIdSeq += 1;
  return -(Date.now() * 1000 + tempMessageIdSeq);
};

const createLocalMessage = (
  id: number,
  role: MessageRole,
  content: string,
  files?: ChatFile[],
): Message => ({
  id,
  content,
  role,
  sources: null,
  files,
  createdAt: nowUnix(),
  updatedAt: nowUnix(),
  isDeleted: false,
  deletedAt: 0,
  deletedBy: "",
});

class ChatStore {
  projects: Project[] = [];
  conversations: Conversation[] = [];
  messages: Message[] = [];
  selectedProjectId: number | null = null;
  selectedConversationId: number | null = null;
  streamingMessageId: number | null = null;
  nameUser = "";
  isAiResponding = false;
  isLoadingConversations = false;
  isLoadingMessages = false;

  /** Client-side typewriter queue (not observed — only message content drives UI). */
  private tokenQueue: string[] = [];
  private streamTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingDoneMessageId: number | null = null;
  private streamDrainResolve: (() => void) | null = null;

  constructor() {
    makeAutoObservable<
      ChatStore,
      "tokenQueue" | "streamTimer" | "pendingDoneMessageId" | "streamDrainResolve"
    >(this, {
      tokenQueue: false,
      streamTimer: false,
      pendingDoneMessageId: false,
      streamDrainResolve: false,
    });
  }

  @computed
  get filteredConversations(): Conversation[] {
    return this.conversations;
  }

  @computed
  get selectedProject(): Project | undefined {
    return this.projects.find((p) => p.id === this.selectedProjectId);
  }

  @computed
  get isEmptyChat(): boolean {
    return !this.selectedConversationId && this.messages.length === 0;
  }

  @action
  initFromProfile = (staff: Partial<Staff>) => {
    this.projects = staff.projects ?? [];
    this.nameUser = staff.name || "";
    if (this.projects.length === 0) {
      this.selectedProjectId = null;
      this.conversations = [];
      this.selectedConversationId = null;
      this.messages = [];
      return;
    }
    const stillValid = this.projects.some((p) => p.id === this.selectedProjectId);
    if (!stillValid) {
      this.selectedProjectId = this.projects[0].id;
      this.conversations = [];
      this.selectedConversationId = null;
      this.messages = [];
    }
  };

  @action
  setProjects = (data: Project[]) => {
    this.projects = data;
  };

  @action
  setConversations = (data: Conversation[]) => {
    this.conversations = data;
  };

  @action
  updateConversationName = (id: number, name: string) => {
    this.conversations = this.conversations.map((conversation) =>
      conversation.id === id ? { ...conversation, name } : conversation,
    );
  };

  @action
  renameConversation = async (id: number, name: string) => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return;
    }
    await conversationApi.update(id, { conversation: { name: trimmedName } });
    runInAction(() => {
      this.updateConversationName(id, trimmedName);
    });
  };

  @action
  deleteConversation = async (id: number) => {
    await conversationApi.remove(id);
    runInAction(() => {
      this.conversations = this.conversations.filter((conversation) => conversation.id !== id);
      if (this.selectedConversationId === id) {
        this.selectedConversationId = null;
        this.messages = [];
      }
    });
  };

  @action
  setMessages = (data: Message[]) => {
    this.messages = data;
  };

  @action
  addMessage = (msg: Message) => {
    this.messages = [...this.messages, msg];
  };

  @action
  setNameUser = (name: string) => {
    this.nameUser = name;
  };

  @action
  setIsAiResponding = (value: boolean) => {
    this.isAiResponding = value;
  };

  @action
  setSelectedProject = async (id: number) => {
    if (this.selectedProjectId === id) {
      return;
    }
    this.selectedProjectId = id;
    this.selectedConversationId = null;
    this.messages = [];
    await this.fetchConversations(id);
  };

  @action
  fetchConversations = async (projectId: number) => {
    this.isLoadingConversations = true;
    try {
      const res = await conversationApi.getList({ projectId, page: 1, limit: 50 });
      runInAction(() => {
        this.conversations = (res.data?.conversations as Conversation[]) ?? [];
      });
    } finally {
      runInAction(() => {
        this.isLoadingConversations = false;
      });
    }
  };

  @action
  selectConversation = async (id: number) => {
    this.selectedConversationId = id;
    this.isLoadingMessages = true;
    try {
      const res = await conversationApi.getMessages(id, { page: 1, limit: 100 });
      runInAction(() => {
        this.messages = normalizeMessages((res.data?.messages as Message[]) ?? []);
      });
    } finally {
      runInAction(() => {
        this.isLoadingMessages = false;
      });
    }
  };

  @action
  createConversation = async (projectId: number, name?: string) => {
    const res = await conversationApi.create({ projectId, name });
    const conversation = res.data as Conversation;
    runInAction(() => {
      this.conversations = [conversation, ...this.conversations];
      this.selectedConversationId = conversation.id;
      this.messages = [];
    });
    return conversation;
  };

  @action
  ensureConversation = async () => {
    if (this.selectedConversationId) {
      return this.selectedConversationId;
    }
    if (!this.selectedProjectId) {
      throw new Error("Vui lòng chọn dự án trước khi chat");
    }
    const conversation = await this.createConversation(this.selectedProjectId);
    return conversation.id;
  };

  @action
  addOptimisticUserMessage = (content: string, files?: ChatFile[]) => {
    const msg = createLocalMessage(
      nextTempMessageId(),
      MessageRole.User,
      content,
      files?.length ? files : undefined,
    );
    this.addMessage(msg);
    return msg;
  };

  @action
  startStreamingAssistant = () => {
    this.clearStreamingTypewriter();
    const tempId = nextTempMessageId();
    this.streamingMessageId = tempId;
    this.addMessage(createLocalMessage(tempId, MessageRole.Assistant, ""));
  };

  @action
  updateStreamingMessage = (token: string) => {
    if (this.streamingMessageId == null) {
      return;
    }
    this.messages = this.messages.map((msg) =>
      msg.id === this.streamingMessageId && msg.role === MessageRole.Assistant
        ? { ...msg, content: `${msg.content}${token}` }
        : msg,
    );
  };

  get hasPendingStreamingTokens(): boolean {
    return this.tokenQueue.length > 0;
  }

  @action
  enqueueStreamingToken = (token: string) => {
    if (!token || this.streamingMessageId == null) {
      return;
    }
    this.tokenQueue.push(token);
    this.scheduleDrain();
  };

  @action
  completeStreaming = (messageId: number): Promise<void> => {
    this.pendingDoneMessageId = messageId;

    if (this.tokenQueue.length === 0 && this.streamTimer == null) {
      this.pendingDoneMessageId = null;
      this.finalizeAssistantMessage(messageId);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.streamDrainResolve = resolve;
      this.scheduleDrain();
    });
  };

  @action
  clearStreamingTypewriter = () => {
    if (this.streamTimer != null) {
      clearTimeout(this.streamTimer);
      this.streamTimer = null;
    }
    this.tokenQueue = [];
    this.pendingDoneMessageId = null;
    if (this.streamDrainResolve) {
      const resolve = this.streamDrainResolve;
      this.streamDrainResolve = null;
      resolve();
    }
  };

  private getDrainDelay = () => {
    const backlog = this.tokenQueue.length;
    if (backlog > 120) {
      return 8;
    }
    if (backlog > 40) {
      return 12;
    }
    return 18;
  };

  private getDrainBatch = () => {
    const backlog = this.tokenQueue.length;
    if (backlog > 120) {
      return 6;
    }
    if (backlog > 40) {
      return 3;
    }
    return 1;
  };

  private scheduleDrain = () => {
    if (this.streamTimer != null) {
      return;
    }
    this.streamTimer = setTimeout(() => {
      runInAction(() => {
        this.streamTimer = null;
        this.drainStreamingTokens();
      });
    }, this.getDrainDelay());
  };

  private drainStreamingTokens = () => {
    if (this.tokenQueue.length === 0) {
      if (this.pendingDoneMessageId != null) {
        const messageId = this.pendingDoneMessageId;
        this.pendingDoneMessageId = null;
        this.finalizeAssistantMessage(messageId);
        if (this.streamDrainResolve) {
          const resolve = this.streamDrainResolve;
          this.streamDrainResolve = null;
          resolve();
        }
      }
      return;
    }

    const chunk = this.tokenQueue.splice(0, this.getDrainBatch()).join("");
    this.updateStreamingMessage(chunk);

    if (this.tokenQueue.length > 0 || this.pendingDoneMessageId != null) {
      this.scheduleDrain();
    }
  };

  @action
  finalizeAssistantMessage = (messageId: number) => {
    if (this.streamingMessageId == null) {
      return;
    }
    this.messages = this.messages.map((msg) =>
      msg.id === this.streamingMessageId && msg.role === MessageRole.Assistant
        ? { ...msg, id: messageId }
        : msg,
    );
    this.streamingMessageId = null;
  };

  @action
  removeStreamingAssistant = () => {
    this.clearStreamingTypewriter();
    if (this.streamingMessageId == null) {
      return;
    }
    this.messages = this.messages.filter(
      (msg) => !(msg.id === this.streamingMessageId && msg.role === MessageRole.Assistant),
    );
    this.streamingMessageId = null;
  };

  @action
  startNewChat = () => {
    this.clearStreamingTypewriter();
    this.selectedConversationId = null;
    this.messages = [];
    this.streamingMessageId = null;
  };

  @action
  reset = () => {
    this.clearStreamingTypewriter();
    this.projects = [];
    this.conversations = [];
    this.messages = [];
    this.selectedProjectId = null;
    this.selectedConversationId = null;
    this.streamingMessageId = null;
    this.nameUser = "";
    this.isAiResponding = false;
    this.isLoadingConversations = false;
    this.isLoadingMessages = false;
  };
}

const chatStore = new ChatStore();

export { chatStore };
